import { env } from "./env";

/**
 * Browser Sentry init, called once from each app's entry before React mounts.
 *
 * Replaces Next's `instrumentation-client.ts` file convention. The DSN is
 * public by design (baked into the client bundle); when unset, `init` is
 * skipped so non-configured environments stay silent.
 *
 * PII note: this is an HR app, so the defaults are tightened rather than
 * accepted. `sendDefaultPii` is false (no IPs or request headers).
 *
 * Session Replay is deliberately not enabled. Even in errors-only mode it runs
 * a DOM recorder the whole time, buffering every mutation in memory in case an
 * error follows — a steady CPU cost on the large employee and document tables,
 * paid by every user to capture the rare session that breaks, on screens full
 * of salaries and disciplinary records. Stack traces and breadcrumbs carry the
 * debugging signal without it.
 *
 * The SDK is loaded with a dynamic import rather than statically. Imported at
 * the top of the entry, it was over half of the entry chunk — parsed and
 * executed before the app could paint its first frame, on every cold load, for
 * a tool that only matters once something breaks. The cost is that an error
 * thrown in the few hundred milliseconds before the chunk lands is not reported.
 */
export function initSentry(): void {
  if (!env.sentryDsn) return;

  void loadSentry().then((Sentry) =>
    Sentry.init({
      dsn: env.sentryDsn,
      environment: env.sentryEnvironment,
      sendDefaultPii: false,
      tracesSampleRate: 0.1,
    }),
  );
}

let sdk: Promise<typeof import("./sentry-sdk")> | undefined;

/** The SDK module, fetched once. Calls resolve in the order they were made. */
function loadSentry(): Promise<typeof import("./sentry-sdk")> {
  sdk ??= import("./sentry-sdk");
  return sdk;
}

/**
 * Attaches (or with `null`, clears) the account identity on browser events.
 * A no-op when Sentry is not configured, so the SDK is never fetched for it.
 */
export function identifySentryUser(
  identity: { id: string; email: string; role: string; organizationId?: string | null } | null,
): void {
  if (!env.sentryDsn) return;
  void loadSentry().then((Sentry) => {
    if (!identity) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({ id: identity.id, email: identity.email });
    Sentry.setTags({
      user_role: identity.role,
      ...(identity.organizationId ? { organization_id: identity.organizationId } : {}),
    });
  });
}

/**
 * Reports an error that the UI caught and rendered — a route's error screen —
 * so it still reaches Sentry. Uncaught errors are reported by the SDK itself.
 * A no-op when Sentry is not configured.
 */
export function reportClientError(error: unknown): void {
  if (!env.sentryDsn) return;
  void loadSentry().then((Sentry) => Sentry.captureException(error));
}
